/**
 * English Technology Translations
 * 技术页翻译模块 - 英语
 * 
 * 包含红外技术原理、技术优势、科学原理等翻译
 * 
 * SEO 关键词覆盖：
 * - how does infrared heater work
 * - are infrared heaters safe / energy efficient
 * - infrared vs convection heater
 * - far infrared heater technology
 * - infrared heater health benefits
 */

import type { TechnologyTranslations } from '../../types';

export const enTechnology: TechnologyTranslations = {
  title: 'How Infrared Heaters Work',
  subtitle: 'Discover how far-infrared heating technology delivers more efficient, more comfortable warmth — and why it is safer and healthier than traditional convection heating.',
  section1: {
    title: 'Sun-Like Warmth, Room-Wide',
    desc1: 'Traditional convection heaters warm the air first. Hot air rises to the ceiling, leaving cold spots near the floor, stirring up dust, and drying out the room. This is why many people ask: are infrared heaters better than convection heaters?',
    desc2: 'Far-infrared heaters work on a completely different principle. Like sunlight, they emit invisible electromagnetic waves that warm objects, walls, and people directly — not the air. The surfaces absorb this radiant heat and release it gradually, keeping the entire space evenly warm. This is how infrared heaters achieve up to 99% energy conversion efficiency while using up to 80% less electricity than traditional heating.',
    radiant: 'Radiant Heat Transfer',
  },
  section2: {
    title: 'Core Heating Technology',
    intro: 'Our specially engineered heating element, paired with our patented nano-ceramic coating, delivers exceptional heating performance. This is the technology behind our energy-efficient infrared heaters trusted in 80+ countries.',
    surface: 'Nano-Ceramic Coating',
    surfaceDesc: 'Our patented nano-ceramic coating significantly increases the radiant surface area, with emissivity up to 86%. This means more heat output per watt — the key to our energy-efficient design.',
    rapid: 'Quick Warm-Up',
    rapidDesc: 'High-conductivity aluminum reaches the ideal temperature in as little as 5 minutes. No more waiting for the room to warm up — instant radiant heat when you need it.',
    durability: 'Ultra-Long Lifespan',
    durabilityDesc: 'Engineered for 25+ years of maintenance-free operation. Our far-infrared heating elements are built to last, reducing replacement costs and environmental waste.',
    nanoTitle: 'Nano-Ceramic Technology',
  },
  infraredIntroduction: {
    title: 'Advanced Far-Infrared Technology',
    desc: 'How does an infrared heater work exactly? By harnessing the far-infrared portion of the electromagnetic spectrum (8-15 μm wavelength), our heaters achieve outstanding thermal efficiency. This wavelength is specifically optimized so the human body absorbs heat most effectively — similar to how the sun warms you on a cool day.',
    optimalWavelength: 'Optimal Wavelength for Human Body',
    optimalWavelengthDesc: 'Tuned to the 8-15 μm frequency band that naturally resonates with water molecules in the body. This is why far-infrared heat feels so natural and comfortable — it is the same wavelength the human body itself emits.',
    superiorEfficiency: 'Superior Energy Efficiency',
    superiorEfficiencyDesc: 'Are infrared heaters energy efficient? Yes — far-infrared outperforms near-infrared and mid-infrared in heating efficiency. It provides soft, even warmth without scorching or drying the air. Our heaters convert up to 99% of electricity into usable heat.',
    radiantEnergy: 'Direct Radiant Heat, Zero Waste',
    radiantEnergyDesc: 'Unlike convection heaters that waste energy heating air that rises to the ceiling, infrared heaters release pure radiant heat directly to people and objects. No air circulation means no energy waste, no dust, and no allergens spread.',
  },
  healthBenefits: {
    title: 'Beyond Heating: Health Benefits of Infrared Heaters',
    desc: 'Are infrared heaters good for you? Far-infrared does more than keep you warm. Its unique wavelength offers proven health benefits, making it a healthier alternative to traditional convection and forced-air heating systems.',
    card1: {
      title: 'The "Light of Life"',
      desc: 'Operating in the 8-15 μm band, this heat naturally matches your body\'s own frequency. Far-infrared is the same beneficial energy used in sauna therapy and physiotherapy. The result is gentle warmth that is deeply absorbed and feels completely natural — safe for all-day use.',
    },
    card2: {
      title: 'Clean, Easy Breathing',
      desc: 'Are infrared heaters safe for indoor use? Unlike traditional convection heaters, infrared heaters do not blow air, spread dust, or dry out the room. This makes them ideal for people with allergies, asthma, or respiratory sensitivities. No fans means zero noise and zero airborne particles.',
    },
    card3: {
      title: 'Deep, Soothing Warmth',
      desc: 'Far-infrared thermal waves penetrate deep into the skin, supporting microcirculation, soothing muscles, and easing winter stiffness. This is the same principle behind infrared physiotherapy lamps used in hospitals and sports medicine.',
    },
  },
};
